import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Layers,
  Loader2,
  MoreHorizontal,
  Package,
  Plus,
  Search,
  Tags
} from 'lucide-react';
import { useAuth, useToast } from '@/shared/contexts';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/components/ui/Dialog';
import {
  vendorCatalogApi,
  type SupplierCatalogItem,
  type VendorCategoryRow,
  type VendorRow,
  type VendorSectionRow
} from '@/pages/SuperAdmin/api/vendorCatalogApi';
import { superAdminApi } from '@/pages/SuperAdmin/api/superAdminApi';

type SectionWithCategories = VendorSectionRow & {
  categories: VendorCategoryRow[];
};

type NameDialogState =
  | { kind: 'section-create' }
  | { kind: 'section-edit'; section: VendorSectionRow }
  | { kind: 'category-create'; section: VendorSectionRow }
  | { kind: 'category-edit'; category: VendorCategoryRow }
  | null;

function extractError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      return pick((value as { message: unknown }).message);
    }
    return null;
  };
  return pick(data) || fallback;
}

function vendorDisplayName(v: VendorRow): string {
  return v.business_name?.trim() || v.name?.trim() || 'Vendor';
}

function itemVendorId(item: SupplierCatalogItem): string | null {
  return (
    item.vendor_id ||
    item.vendorId ||
    item.offerings?.[0]?.vendorId ||
    item.offerings?.[0]?.vendor?.id ||
    null
  );
}

export const SuperAdminSupplierCatalogPage: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const { accessToken } = useAuth();
  const { notify } = useToast();

  const [supplierName, setSupplierName] = useState('Supplier');
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [sections, setSections] = useState<SectionWithCategories[]>([]);
  const [items, setItems] = useState<SupplierCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionVendorId, setSelectionVendorId] = useState<string | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  const [nameDialog, setNameDialog] = useState<NameDialogState>(null);
  const [nameValue, setNameValue] = useState('');
  const [sectionVendorId, setSectionVendorId] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');

  const load = useCallback(async () => {
    if (!accessToken || !supplierId) return;
    setLoading(true);
    setError(null);
    try {
      const [detailRes, vendorsRes, sectionsRes, itemsRes] = await Promise.all([
        superAdminApi.getSupplierDetail(accessToken, supplierId),
        vendorCatalogApi.listVendors(accessToken, supplierId),
        vendorCatalogApi.listSections(accessToken, supplierId),
        vendorCatalogApi.listItems(accessToken, supplierId)
      ]);

      const supplier = detailRes.data.supplier;
      setSupplierName(
        supplier.businessName ||
          [supplier.firstName, supplier.lastName].filter(Boolean).join(' ') ||
          supplier.email ||
          'Supplier'
      );

      const vendorList = vendorsRes.data.data ?? [];
      setVendors(vendorList);

      const sectionList = (sectionsRes.data.data ?? []).filter(
        s => s.status !== 'archived'
      );
      const withCats = await Promise.all(
        sectionList.map(async section => {
          const catRes = await vendorCatalogApi.listCategories(
            accessToken,
            supplierId,
            section.id
          );
          return {
            ...section,
            categories: (catRes.data.data ?? []).filter(
              c => c.status !== 'archived'
            )
          };
        })
      );
      withCats.sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          a.name.localeCompare(b.name)
      );
      setSections(withCats);
      setItems(itemsRes.data.data ?? []);

      setExpandedVendors(new Set(vendorList.map(v => v.id)));
    } catch (err) {
      setError(extractError(err, 'Failed to load catalog'));
    } finally {
      setLoading(false);
    }
  }, [accessToken, supplierId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => (i.item_name || '').toLowerCase().includes(q));
  }, [items, search]);

  const vendorsWithSections = useMemo(() => {
    const ids = new Set(sections.map(s => s.vendor_id));
    return vendors.filter(v => ids.has(v.id));
  }, [vendors, sections]);

  const uncategorized = useMemo(
    () =>
      filteredItems.filter(
        i => !i.vendor_category_id || i.vendor_category_id.trim() === ''
      ),
    [filteredItems]
  );

  const bulkCategoryOptions = useMemo(() => {
    if (!selectionVendorId) return [];
    return sections
      .filter(s => s.vendor_id === selectionVendorId)
      .flatMap(s =>
        s.categories.map(c => ({
          id: c.id,
          label: `${s.name} · ${c.name}`
        }))
      );
  }, [sections, selectionVendorId]);

  const toggleSet = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const openNameDialog = (state: Exclude<NameDialogState, null>) => {
    if (state.kind === 'section-create') {
      setNameValue('');
      setSectionVendorId(vendors[0]?.id || '');
    } else if (state.kind === 'section-edit') {
      setNameValue(state.section.name);
    } else if (state.kind === 'category-create') {
      setNameValue('');
    } else {
      setNameValue(state.category.name);
    }
    setNameDialog(state);
  };

  const submitNameDialog = async () => {
    if (!accessToken || !supplierId || !nameDialog) return;
    const name = nameValue.trim();
    if (!name) {
      notify('error', 'Name is required');
      return;
    }
    setBusy(true);
    try {
      if (nameDialog.kind === 'section-create') {
        if (!sectionVendorId) {
          notify('error', 'Select a vendor');
          return;
        }
        await vendorCatalogApi.createSection(accessToken, supplierId, {
          vendor_id: sectionVendorId,
          name
        });
        notify('success', 'Section created');
      } else if (nameDialog.kind === 'section-edit') {
        await vendorCatalogApi.updateSection(
          accessToken,
          supplierId,
          nameDialog.section.id,
          { name }
        );
        notify('success', 'Section updated');
      } else if (nameDialog.kind === 'category-create') {
        await vendorCatalogApi.createCategory(
          accessToken,
          supplierId,
          nameDialog.section.id,
          { name }
        );
        notify('success', 'Category created');
      } else {
        await vendorCatalogApi.updateCategory(
          accessToken,
          supplierId,
          nameDialog.category.id,
          { name }
        );
        notify('success', 'Category updated');
      }
      setNameDialog(null);
      await load();
    } catch (err) {
      notify('error', extractError(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const deleteSection = async (section: VendorSectionRow) => {
    if (!accessToken || !supplierId) return;
    if (!window.confirm(`Delete section “${section.name}”?`)) return;
    setBusy(true);
    try {
      await vendorCatalogApi.deleteSection(
        accessToken,
        supplierId,
        section.id
      );
      notify('success', 'Section deleted');
      await load();
    } catch (err) {
      notify('error', extractError(err, 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  const deleteCategory = async (category: VendorCategoryRow) => {
    if (!accessToken || !supplierId) return;
    if (!window.confirm(`Delete category “${category.name}”?`)) return;
    setBusy(true);
    try {
      await vendorCatalogApi.deleteCategory(
        accessToken,
        supplierId,
        category.id
      );
      notify('success', 'Category deleted');
      await load();
    } catch (err) {
      notify('error', extractError(err, 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleItemSelect = (item: SupplierCatalogItem) => {
    const vendorId = itemVendorId(item);
    if (!vendorId) {
      notify('error', 'Item has no vendor — cannot bulk-assign');
      return;
    }
    if (selectionVendorId && selectionVendorId !== vendorId) {
      notify('error', 'Select items from one vendor at a time');
      return;
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
        if (next.size === 0) setSelectionVendorId(null);
      } else {
        next.add(item.id);
        setSelectionVendorId(vendorId);
      }
      return next;
    });
  };

  const submitBulkAssign = async () => {
    if (!accessToken || !supplierId || selectedIds.size === 0) return;
    setBusy(true);
    try {
      await vendorCatalogApi.bulkAssignItemCategory(
        accessToken,
        supplierId,
        [...selectedIds],
        bulkCategoryId || null
      );
      notify('success', `Updated ${selectedIds.size} item(s)`);
      setBulkDialogOpen(false);
      setSelectMode(false);
      setSelectedIds(new Set());
      setSelectionVendorId(null);
      await load();
    } catch (err) {
      notify('error', extractError(err, 'Bulk assign failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to={`/super-admin/suppliers/${supplierId}`}
            className="mb-2 inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-[#006B82]"
          >
            <ArrowLeft className="h-4 w-4" />
            {supplierName}
          </Link>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
            Sections & categories
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            Vendor → section → category → items (same as Flutter supplier catalog)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSelectMode(m => !m);
              setSelectedIds(new Set());
              setSelectionVendorId(null);
            }}
          >
            {selectMode ? 'Cancel select' : 'Select items'}
          </Button>
          <Button
            type="button"
            className="bg-[#006B82] hover:bg-[#005a6e]"
            onClick={() => openNameDialog({ kind: 'section-create' })}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Section
          </Button>
        </div>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items…"
          className="pl-9"
        />
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-primary bg-background-card px-4 py-3">
          <p className="text-sm text-text-secondary">
            {selectedIds.size} selected
          </p>
          <Button
            type="button"
            className="bg-[#006B82] hover:bg-[#005a6e]"
            disabled={busy}
            onClick={() => {
              setBulkCategoryId('');
              setBulkDialogOpen(true);
            }}
          >
            Assign category
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {vendorsWithSections.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-primary bg-background-card px-6 py-12 text-center">
            <Layers className="mx-auto h-8 w-8 text-text-muted" />
            <p className="mt-3 font-medium text-text-primary">No sections yet</p>
            <p className="mt-1 text-sm text-text-tertiary">
              Create a section for a vendor to start organizing items.
            </p>
          </div>
        )}

        {vendorsWithSections.map(vendor => {
          const vendorSections = sections.filter(s => s.vendor_id === vendor.id);
          const open = expandedVendors.has(vendor.id);
          return (
            <div
              key={vendor.id}
              className="overflow-hidden rounded-2xl border border-border-primary bg-background-card shadow-sm"
            >
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-background-hover"
                onClick={() =>
                  setExpandedVendors(prev => toggleSet(prev, vendor.id))
                }
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 text-text-muted" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-text-muted" />
                )}
                <Package className="h-4 w-4 text-[#006B82]" />
                <span className="font-semibold text-text-primary">
                  {vendorDisplayName(vendor)}
                </span>
                <span className="text-xs text-text-muted">
                  {vendorSections.length} section
                  {vendorSections.length === 1 ? '' : 's'}
                </span>
              </button>

              {open && (
                <div className="space-y-1 border-t border-border-secondary px-2 py-2">
                  {vendorSections.map(section => {
                    const secOpen = expandedSections.has(section.id);
                    const itemCount = section.categories.reduce(
                      (n, c) =>
                        n +
                        filteredItems.filter(
                          i => i.vendor_category_id === c.id
                        ).length,
                      0
                    );
                    return (
                      <div key={section.id} className="rounded-xl">
                        <div className="flex items-center gap-1 px-2 py-1.5">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            onClick={() =>
                              setExpandedSections(prev =>
                                toggleSet(prev, section.id)
                              )
                            }
                          >
                            {secOpen ? (
                              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                            )}
                            <Tags className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                            <span className="truncate text-sm font-medium text-text-primary">
                              {section.name}
                            </span>
                            <span className="text-xs text-text-muted">
                              {itemCount} items
                            </span>
                          </button>
                          <SectionMenu
                            onAddCategory={() =>
                              openNameDialog({
                                kind: 'category-create',
                                section
                              })
                            }
                            onEdit={() =>
                              openNameDialog({ kind: 'section-edit', section })
                            }
                            onDelete={() => void deleteSection(section)}
                          />
                        </div>

                        {secOpen && (
                          <div className="ml-6 space-y-1 pb-2">
                            {section.categories.length === 0 && (
                              <p className="px-2 py-1 text-xs text-text-muted">
                                No categories
                              </p>
                            )}
                            {section.categories.map(category => {
                              const catItems = filteredItems.filter(
                                i => i.vendor_category_id === category.id
                              );
                              const catOpen = expandedCategories.has(
                                category.id
                              );
                              return (
                                <div key={category.id}>
                                  <div className="flex items-center gap-1 px-2 py-1">
                                    <button
                                      type="button"
                                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                      onClick={() =>
                                        setExpandedCategories(prev =>
                                          toggleSet(prev, category.id)
                                        )
                                      }
                                    >
                                      {catOpen ? (
                                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                                      )}
                                      <span className="truncate text-sm text-text-secondary">
                                        {category.name}
                                      </span>
                                      <span className="text-xs text-text-muted">
                                        {catItems.length}
                                      </span>
                                    </button>
                                    <SectionMenu
                                      editLabel="Edit category"
                                      deleteLabel="Delete category"
                                      onEdit={() =>
                                        openNameDialog({
                                          kind: 'category-edit',
                                          category
                                        })
                                      }
                                      onDelete={() =>
                                        void deleteCategory(category)
                                      }
                                    />
                                  </div>
                                  {catOpen && (
                                    <ul className="ml-6 space-y-0.5">
                                      {catItems.map(item => (
                                        <ItemRow
                                          key={item.id}
                                          item={item}
                                          selectMode={selectMode}
                                          selected={selectedIds.has(item.id)}
                                          onToggle={() =>
                                            toggleItemSelect(item)
                                          }
                                        />
                                      ))}
                                      {catItems.length === 0 && (
                                        <li className="px-2 py-1 text-xs text-text-muted">
                                          No items
                                        </li>
                                      )}
                                    </ul>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {uncategorized.length > 0 && (
          <div className="rounded-2xl border border-border-primary bg-background-card p-4 shadow-sm">
            <h3 className="mb-2 text-sm font-semibold text-text-primary">
              Uncategorized ({uncategorized.length})
            </h3>
            <ul className="space-y-0.5">
              {uncategorized.map(item => (
                <ItemRow
                  key={item.id}
                  item={item}
                  selectMode={selectMode}
                  selected={selectedIds.has(item.id)}
                  onToggle={() => toggleItemSelect(item)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog
        open={!!nameDialog}
        onOpenChange={next => !next && !busy && setNameDialog(null)}
      >
        <DialogContent
          className="w-[min(100vw-2rem,28rem)]"
          onClose={() => !busy && setNameDialog(null)}
        >
          <DialogHeader>
            <DialogTitle>
              {nameDialog?.kind === 'section-create' && 'New section'}
              {nameDialog?.kind === 'section-edit' && 'Edit section'}
              {nameDialog?.kind === 'category-create' && 'New category'}
              {nameDialog?.kind === 'category-edit' && 'Edit category'}
            </DialogTitle>
            <DialogDescription>
              {nameDialog?.kind === 'section-create'
                ? 'Pick a vendor and name the section.'
                : 'Enter a display name.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {nameDialog?.kind === 'section-create' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-text-tertiary">
                  Vendor
                </label>
                <select
                  className="h-10 w-full rounded-md border border-border-primary bg-background-card px-3 text-sm text-text-primary"
                  value={sectionVendorId}
                  onChange={e => setSectionVendorId(e.target.value)}
                >
                  {vendors.length === 0 && (
                    <option value="">No vendors</option>
                  )}
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>
                      {vendorDisplayName(v)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-text-tertiary">
                Name
              </label>
              <Input
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                autoFocus
                disabled={busy}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setNameDialog(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#006B82] hover:bg-[#005a6e]"
              disabled={busy || !nameValue.trim()}
              onClick={() => void submitNameDialog()}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkDialogOpen}
        onOpenChange={next => !next && !busy && setBulkDialogOpen(false)}
      >
        <DialogContent
          className="w-[min(100vw-2rem,28rem)]"
          onClose={() => !busy && setBulkDialogOpen(false)}
        >
          <DialogHeader>
            <DialogTitle>Assign category</DialogTitle>
            <DialogDescription>
              Apply to {selectedIds.size} selected item(s). Choose Unassigned to
              clear.
            </DialogDescription>
          </DialogHeader>
          <select
            className="h-10 w-full rounded-md border border-border-primary bg-background-card px-3 text-sm text-text-primary"
            value={bulkCategoryId}
            onChange={e => setBulkCategoryId(e.target.value)}
          >
            <option value="">Unassigned</option>
            {bulkCategoryOptions.map(o => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setBulkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#006B82] hover:bg-[#005a6e]"
              disabled={busy}
              onClick={() => void submitBulkAssign()}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ItemRow: React.FC<{
  item: SupplierCatalogItem;
  selectMode: boolean;
  selected: boolean;
  onToggle: () => void;
}> = ({ item, selectMode, selected, onToggle }) => (
  <li className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text-secondary hover:bg-background-hover">
    {selectMode && (
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="h-4 w-4 rounded border border-border-primary"
      />
    )}
    <span className="truncate">{item.item_name || 'Untitled item'}</span>
  </li>
);

const SectionMenu: React.FC<{
  onAddCategory?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  editLabel?: string;
  deleteLabel?: string;
}> = ({
  onAddCategory,
  onEdit,
  onDelete,
  editLabel = 'Edit section',
  deleteLabel = 'Delete section'
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="rounded-md p-1.5 text-text-muted hover:bg-background-hover hover:text-text-primary"
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        aria-label="Actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 min-w-[10rem] rounded-lg border border-border-primary bg-background-card py-1 text-sm shadow-lg">
            {onAddCategory && (
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-text-primary hover:bg-background-hover"
                onClick={() => {
                  setOpen(false);
                  onAddCategory();
                }}
              >
                Add category
              </button>
            )}
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-text-primary hover:bg-background-hover"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              {editLabel}
            </button>
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-accent-error hover:bg-background-hover"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              {deleteLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
